import { createStore, createEffect, createEvent, combine, sample } from 'effector';
import { $userCardsStore } from './userCardsStore';
import { $notificationStore } from '../notificationStore';
import { ApiUtilsV2 } from '@shared/utils';
import {
  RequestStrategyEnum,
  UsersApi,
  type DeleteUserCardsRequest,
  type UpdatePasswordRequest,
  type UserResponse,
  type UserUpdateRequest,
  type WithSignal,
} from '@shared/api';

const getUserEffect = createEffect(({ signal, ...request }: WithSignal<{ id: string }>) => UsersApi.getUser(request.id, signal));
const updateUserEffect = createEffect((request: UserUpdateRequest) => UsersApi.updateUser(request));
const changePasswordEffect = createEffect((request: UpdatePasswordRequest) => UsersApi.updatePassword(request));
const lockUserEffect = createEffect((id: string) => UsersApi.lockUser(id));
const unlockUserEffect = createEffect((id: string) => UsersApi.unlockUser(id));
const deleteUserEffect = createEffect((id: string) => UsersApi.deleteUser(id));
const deleteCardEffect = createEffect(async (params: { request: DeleteUserCardsRequest }) => {
  const user = await UsersApi.deleteUserCards(params.request);
  if (!user.success) throw new Error(user.message || 'Error deleting user');
  return { AssignedUserId: params.request.userIds };
});
const clearEvent = createEvent();

sample({ source: deleteCardEffect.doneData, target: $userCardsStore.events.getUserCards });

const $dataStore = createStore<UserResponse | null>(null)
  .on(getUserEffect.done, (current, data) => data.result.data || current)
  .on(updateUserEffect.done, (current, data) => data.result.data || current)
  .on(lockUserEffect.done, (current) => (current ? { ...current, locked: true } : current))
  .on(unlockUserEffect.done, (current) => (current ? { ...current, locked: false } : current))
  .on(deleteUserEffect.done, () => null)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getUserEffect.pending, (_, data) => data)
  .on(getUserEffect.fail, () => false)
  .on(getUserEffect.done, () => false)
  .on(deleteUserEffect.pending, (_, data) => data)
  .on(deleteUserEffect.fail, () => false)
  .on(deleteUserEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getUserEffect, () => null)
  // .on(getUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getUserEffect.fail, (_, data) => {
    if (data.error.message === 'Action is forbidden.') return 'Action is forbidden.';
    if (data.error.message === 'User Id cannot be empty.') return 'Action is forbidden.';
    return 'Something went wrong while fetching User. Please try again.';
  })
  .on(getUserEffect.done, () => null)
  .reset(clearEvent);

const $actionErrorStore = createStore<string | null>(null)
  .on(deleteUserEffect, () => null)
  // .on(deleteUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteUserEffect.fail, () => 'Something went wrong while deleting User. Please try again.')
  .on(deleteUserEffect.done, () => null)
  .on(updateUserEffect, () => null)
  // .on(updateUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateUserEffect.fail, () => 'Something went wrong while updating User. Please try again.')
  .on(updateUserEffect.done, () => null)
  .on(changePasswordEffect, () => null)
  // .on(changePasswordEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(changePasswordEffect.fail, () => 'Something went wrong while changing Password. Please try again.')
  .on(changePasswordEffect.done, () => null)
  .on(lockUserEffect, () => null)
  // .on(lockUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(lockUserEffect.fail, () => 'Something went wrong while locking User. Please try again.')
  .on(lockUserEffect.done, () => null)
  .on(unlockUserEffect, () => null)
  // .on(unlockUserEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(unlockUserEffect.fail, () => 'Something went wrong while unlocking User. Please try again.')
  .on(unlockUserEffect.done, () => null)
  .on(deleteCardEffect, () => null)
  // .on(deleteCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteCardEffect.fail, () => 'Something went wrong while removing Card. Please try again.')
  .on(deleteCardEffect.done, () => null)
  .reset(clearEvent);
$actionErrorStore.watch((error) => $notificationStore.events.setError(error));

const getUserApi = ApiUtilsV2.wrapperApi(getUserEffect, RequestStrategyEnum.Abort);
clearEvent.watch(() => getUserApi.cancel());

export const $userStore = {
  events: {
    getUser: getUserApi.trigger,
    updateUser: updateUserEffect,
    changePassword: changePasswordEffect,
    lockUser: lockUserEffect,
    unlockUser: unlockUserEffect,
    deleteUser: deleteUserEffect,
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
